import { StyleObject } from 'styletron-standard';

/**
 * Returns focus-visible styles using a standard outline.
 *
 * @param selector - CSS selector to apply the outline on. Defaults to ':focus-visible'.
 *   Use ':has(input:focus-visible)' for wrapper elements that delegate focus to a child input.
 */
const getFocusRingStyles = (selector = ':focus-visible'): StyleObject => ({
    outline: '2px solid transparent',
    outlineOffset: '-2px',
    [selector]: {
        outline: '2px solid var(--color-outline-focus)',
    },
});

/**
 * Returns focus-visible styles using an inset ::after pseudo-element.
 * Use for interactive elements inside overflow:hidden containers where
 * a standard outline would be clipped.
 *
 * @param targetSelector - Optional child selector to place the focus ring on (e.g. '> div:last-child').
 *   When provided, the ring appears on the matched child instead of the element itself.
 */
const getInsetFocusRingStyles = (targetSelector?: string): StyleObject => {
    const afterStyles = {
        content: '""',
        position: 'absolute',
        inset: '0',
        borderRadius: 'inherit',
        boxShadow: 'inset 0 0 0 2px var(--color-outline-focus)',
        pointerEvents: 'none',
        zIndex: 2,
    };

    if (targetSelector) {
        return {
            ':focus-visible': {
                outline: '2px solid transparent',
            },
            [`:focus-visible ${targetSelector}`]: {
                position: 'relative',
            },
            [`:focus-visible ${targetSelector}::after`]: afterStyles,
        };
    }

    return {
        position: 'relative',
        ':focus-visible': {
            outline: '2px solid transparent',
            '::after': afterStyles,
        },
    };
};

export { getFocusRingStyles, getInsetFocusRingStyles };
